import { useState } from 'react'
import { DateTime } from 'luxon'
import { SortByInput, SortOrder, useMachinePartsHistoryQuery } from '@/buyers/_gen/gql'
import RequestPartButton from '@/buyers/components/RequestPartButton'
import useGqlClient from '@/buyers/hooks/useGqlClient'
import useSession from '@/buyers/hooks/useSession'
import { Table, Td, Th, Tr } from '@/gf/components/next/Table'
import SortByHeaderButton from '@/gf/components/SortByHeaderButton'
import Time from '@/gf/modules/Time'
import PaginationC from '@/gf/components/Pagination'
import usePage from '@/gf/hooks/usePage'
import { InformationCircleIcon } from '@heroicons/react/24/outline'
import { SimpleTooltip } from '@/gf/components/next/Tooltip'
import Link from '@/gf/components/Link'
import pluralize from 'pluralize'
import SearchInput from '@/gf/components/SearchInput'
import { StringParam, useQueryParam, withDefault } from 'use-query-params'

const Parts = ({ orgMachineId }: { orgMachineId: string }) => {
  const { orgId } = useSession()
  const client = useGqlClient()
  const [searchTerm, setSearchTerm] = useQueryParam('search', withDefault(StringParam, ''))
  const [page, setPage] = usePage()
  const [sortBy, setSortBy] = useState<SortByInput>({
    field: 'last_requested_at',
    order: SortOrder.Desc,
  })

  const onSearchChanged = (newValue: string) => {
    setSearchTerm(newValue, 'replaceIn')
    setPage(1)
  }

  const { data } = useMachinePartsHistoryQuery({
    client,
    variables: {
      orgMachineId,
      page,
      orgId,
      sortBy,
      search: searchTerm,
    },
  })

  return (
    <div className="space-y-4">
      <p className="text-sm">
        Below is a list of parts associated with this machine that you have previously ordered.
      </p>
      <div>
        <SearchInput
          value={searchTerm}
          onChange={onSearchChanged}
          placeholder="Search by part number or part name"
          autoFocus
          autoCompleteOff
        />
      </div>
      <Table className="text-sm">
        <thead>
          <Tr>
            <Th>Part Number</Th>
            <Th>
              <SortByHeaderButton
                display="Part Name"
                field="part_description"
                sortBy={sortBy}
                setSortBy={setSortBy}
              />
            </Th>
            <Th>
              <SortByHeaderButton
                display="Orders"
                field="num_of_orders"
                sortBy={sortBy}
                setSortBy={setSortBy}
              />
            </Th>
            <Th>
              <SortByHeaderButton
                display="Quantity"
                field="total_qty"
                sortBy={sortBy}
                setSortBy={setSortBy}
              />
            </Th>
            <Th>
              <SortByHeaderButton
                display="Last Request"
                field="last_requested_at"
                sortBy={sortBy}
                setSortBy={setSortBy}
              />
            </Th>
            <Th>
              <SortByHeaderButton
                display="Frequency"
                field="request_frequency"
                sortBy={sortBy}
                setSortBy={setSortBy}
              />
            </Th>
            <Th>
              <SortByHeaderButton
                display="Predicted Need"
                field="needed_by"
                sortBy={sortBy}
                setSortBy={setSortBy}
              />
            </Th>
            <Th className="w-32" />
          </Tr>
        </thead>
        <tbody>
          {data ? (
            data.periodicPartRequests.pagination.totalResults === 0 ? (
              <Tr>
                <Td colSpan={7}>
                  {searchTerm ? (
                    <>
                      No results found for your search term:{' '}
                      <span className="font-medium">{searchTerm}</span>{' '}
                    </>
                  ) : (
                    'No orders have been created for this machine'
                  )}
                </Td>
              </Tr>
            ) : (
              data.periodicPartRequests.entries.map((p) => (
                <Tr key={p.id}>
                  <Td>{p.partNumber}</Td>
                  <Td>{p.partDescription} </Td>
                  <Td>
                    <Link.T
                      to={`/reporting/consumption?selected=${p.id}&search=${p.partNumber}`}
                      target="_blank"
                    >
                      {p.numOfOrders} {pluralize('order', p.numOfOrders)}
                    </Link.T>{' '}
                  </Td>
                  <Td>{p.totalQty}</Td>
                  <Td>{p.lastRequestedAt.toLocaleString(DateTime.DATE_SHORT)} </Td>
                  <Td>
                    {p.requestFrequencySec ? (
                      `${p.requestQty} every ${Time.secondsToString2(p.requestFrequencySec)}`
                    ) : (
                      <SimpleTooltip
                        theme="light"
                        placement="top"
                        className="max-w-[19rem] text-center"
                        text="We haven't seen enough orders of this part to make a reliable prediction."
                      >
                        <span className="flex gap-x-2 items-center">
                          <InformationCircleIcon className="h-4 w-4 text-blue-600" /> No frequency
                        </span>
                      </SimpleTooltip>
                    )}
                  </Td>
                  <Td>
                    {p.neededBy ? (
                      <>
                        {p.neededBy.toRelative()}
                        <span className="text-xs text-gray-600 block">
                          {p.neededBy.toLocaleString(DateTime.DATE_SHORT)}
                        </span>
                      </>
                    ) : (
                      'No current prediction'
                    )}
                  </Td>
                  <Td>
                    <RequestPartButton
                      partNumber={p.partNumber}
                      partDescription={p.partDescription}
                      requestQty={p.requestQty ?? 1}
                      orgMachineId={orgMachineId}
                    />
                  </Td>
                </Tr>
              ))
            )
          ) : (
            <Tr>
              <Td colSpan={7}>Loading...</Td>
            </Tr>
          )}
        </tbody>
      </Table>
      <PaginationC
        page={page}
        pagination={data?.periodicPartRequests.pagination}
        updatePage={setPage}
      />
    </div>
  )
}

export default Parts
